import { z } from 'zod';

// Social link validation schemas
const emailSchema = z.union([z.literal(''), z.string().trim().email('Please enter a valid email address')]);

const usernameSchema = (minLength = 1, maxLength = 100) =>
    z.union([
        z.literal(''),
        z
            .string()
            .trim()
            .min(minLength, `Username must be at least ${minLength} characters`)
            .max(maxLength, `Username must be no more than ${maxLength} characters`)
            .regex(/^@?[a-zA-Z0-9._]+$/, 'Username can only contain letters, numbers, dots, underscores'),
    ]);

// Platform-specific URL schemas
const facebookUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?facebook\.com\/.+$/, 'Please enter a valid Facebook URL'),
]);

const youtubeUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)\/.+$/, 'Please enter a valid YouTube URL'),
]);

const linkedinUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?linkedin\.com\/in\/.+$/, 'Please enter a valid LinkedIn URL'),
]);

const pinterestUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?pinterest\.com\/.+$/, 'Please enter a valid Pinterest URL'),
]);

const spotifyUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/open\.spotify\.com\/.+$/, 'Please enter a valid Spotify URL'),
]);

const applePodcastsUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/podcasts\.apple\.com\/.+$/, 'Please enter a valid Apple Podcasts URL'),
]);

const etsyUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?etsy\.com\/.+$/, 'Please enter a valid Etsy URL'),
]);

const discordUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(
            /^https?:\/\/(www\.)?(discord\.gg|discord\.com\/invite)\/.+$/,
            'Please enter a valid Discord invite URL'
        ),
]);

const snapchatUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?snapchat\.com\/.+$/, 'Please enter a valid Snapchat URL'),
]);

const twitchUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?twitch\.tv\/.+$/, 'Please enter a valid Twitch URL'),
]);

const vimeoUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/(www\.)?vimeo\.com\/.+$/, 'Please enter a valid Vimeo URL'),
]);

// Generic URL schema for custom links
const genericUrlSchema = z.union([
    z.literal(''),
    z
        .string()
        .trim()
        .url('Please enter a valid URL')
        .regex(/^https?:\/\/.+$/, 'URL must start with http:// or https://'),
]);

// Social links schema - exported for reuse in subscription flow
export const socialLinksSchema = z.object({
    instagram: usernameSchema(1, 30),
    tiktok: usernameSchema(1, 24),
    email: emailSchema,
    facebook: facebookUrlSchema,
    youtube: youtubeUrlSchema,
    link: genericUrlSchema,
    pinterest: pinterestUrlSchema,
    twitter: usernameSchema(1, 15),
    spotify: spotifyUrlSchema,
    'apple-podcasts': applePodcastsUrlSchema,
    etsy: etsyUrlSchema,
    discord: discordUrlSchema,
    snapchat: snapchatUrlSchema,
    twitch: twitchUrlSchema,
    vimeo: vimeoUrlSchema,
    linkedin: linkedinUrlSchema,
});

export type SocialLinksValues = z.infer<typeof socialLinksSchema>;

// Profile form schema
export const profileFormSchema = z.object({
    displayName: z
        .string()
        .trim()
        .min(1, 'Display name is required')
        .min(2, 'Display name must be at least 2 characters')
        .max(50, 'Display name must be no more than 50 characters'),
    bio: z.string().trim().max(80, 'Bio must be no more than 80 characters'),
    socialLinks: socialLinksSchema,
});

export type ProfileFormValues = z.infer<typeof profileFormSchema>;
